import {
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  ListObjectsV2CommandInput,
  ListObjectsV2CommandOutput,
  PutObjectCommand,
  PutObjectCommandOutput,
  S3Client,
  S3ClientConfig,
  S3ServiceException
} from '@aws-sdk/client-s3';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';
import { eErrorMessages } from 'src/constants/generic-constants';
import { logger } from '../analytics/KatalLogger';
import { getAWSServiceCredentials } from './auth-token-services';
import { getMoment, getTimeDifference } from './date-time-utilities';
import { S3DeleteResult } from 'src/models/AppContextModels';
import { isCorpSegmentS3URI, getFileFromCorpSegmentCdn } from './cdn-fetch-services';

// Singleton S3 client — reused across all calls to avoid repeated Cognito+STS round-trips
// Credentials are provided via getAWSServiceCredentials which caches internally and handles expiry.
let cachedS3Client: S3Client | null = null;

export const getS3Client = async () => {
  if (!cachedS3Client) {
    const s3ClientConfig: S3ClientConfig = {
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: getAWSServiceCredentials
    };
    cachedS3Client = new S3Client(s3ClientConfig);
  }
  return cachedS3Client;
};

// In-flight request cache — deduplicates concurrent fetches for the same S3 URI
const inFlightRequests = new Map<string, Promise<JSON>>();

export const getListOfObjectsFromKeys = async (bucket: string, s3Key: string) => {
  const s3Client = await getS3Client();
  const listCommandInput: ListObjectsV2CommandInput = {
    Bucket: bucket,
    Prefix: s3Key
  };
  const listCommand = new ListObjectsV2Command(listCommandInput);
  const listOfObjects = await s3Client.send(listCommand);
  return listOfObjects;
};

export const getAllObjectKeysFromS3 = async (bucket: string, s3Key: string): Promise<string[]> => {
  const s3Client = await getS3Client();
  let allKeys: string[] = [];
  let continuationToken: string | undefined;

  try {
    do {
      const listCommandInput: ListObjectsV2CommandInput = {
        Bucket: bucket,
        Prefix: s3Key,
        ContinuationToken: continuationToken
      };

      const listCommand = new ListObjectsV2Command(listCommandInput);
      let listOfObjects: ListObjectsV2CommandOutput;

      try {
        listOfObjects = await s3Client.send(listCommand);
      } catch (error) {
        if (error instanceof S3ServiceException) {
          logger.error(`S3 service error: ${error.message}`);
          throw new Error(`Failed to list objects: ${error.message}`);
        }
        logger.error(`Unexpected error: ${error}`);
        throw new Error('An unexpected error occurred while listing objects');
      }

      if (listOfObjects.Contents) {
        const keys = listOfObjects.Contents.map((obj) => obj.Key).filter((key): key is string => key !== undefined);
        allKeys = allKeys.concat(keys);
      }

      continuationToken = listOfObjects.NextContinuationToken;
    } while (continuationToken);

    logger.info(`Successfully retrieved ${allKeys.length} keys from bucket ${bucket} with prefix ${s3Key}`);
    return allKeys;
  } catch (error) {
    logger.error(`Error in getAllObjectKeysFromS3: ${error}`);
    throw error;
  }
};

/**
 * Parses an S3 URI to extract the bucket name and key.
 * @param s3URI The S3 URI to parse (e.g., s3://bucket-name/key).
 * @returns An object containing the bucket name and key.
 * @throws Will throw an error if the S3 URI is invalid.
 */
export const parseS3URI = (s3URI: string): { bucket: string; s3Key: string } => {
  const match = s3URI.match(/^s3:\/\/([^/]+)\/(.+)/);
  if (match) {
    const [, bucket, s3Key] = match;
    return {
      bucket,
      s3Key
    };
  } else {
    throw new Error(`Invalid S3 URI: ${s3URI}`);
  }
};

/**
 * Downloads a file from S3 using its URI.
 * Uses in-flight request deduplication — concurrent calls for the same URI share a single fetch.
 * @param s3URI The S3 URI of the file to download (e.g., s3://bucket-name/key).
 * @param signal Optional AbortSignal for cancelling the request
 * @returns The parsed JSON data from the file.
 * @throws Will throw an error if there is an issue downloading the file.
 */
export const getFileFromS3URI = async (s3URI: string, signal?: AbortSignal): Promise<JSON> => {
  // Route corp segment files through CloudFront CDN (no Cognito overhead, edge-cached)
  if (await isCorpSegmentS3URI(s3URI)) {
    try {
      return await getFileFromCorpSegmentCdn(s3URI, signal);
    } catch (error: any) {
      // Don't fallback on abort or known data errors
      if (error.name === 'AbortError' || error.message === eErrorMessages.NO_DATA_FOUND) {
        throw error;
      }
      // Fallback to direct S3 on CDN errors (e.g., 403 from encryption mismatch)
      logger.warn('CDN fetch failed, falling back to direct S3', { s3URI, error: error.message });
    }
  }

  // Deduplication: if an identical request is already in-flight, return the same promise
  const existing = inFlightRequests.get(s3URI);
  if (existing && !signal) {
    return existing;
  }

  const fetchPromise = fetchFileFromS3(s3URI, signal).finally(() => {
    inFlightRequests.delete(s3URI);
  });

  // Only cache non-abortable requests (abortable ones are user-specific)
  if (!signal) {
    inFlightRequests.set(s3URI, fetchPromise);
  }

  return fetchPromise;
};

const fetchFileFromS3 = async (s3URI: string, signal?: AbortSignal): Promise<JSON> => {
  const startTime = getMoment();

  try {
    const s3Client = await getS3Client();
    const { bucket, s3Key } = parseS3URI(s3URI);

    if (signal?.aborted) {
      throw new Error('Request was aborted');
    }

    const getCommand = new GetObjectCommand({
      Bucket: bucket,
      Key: s3Key,
      ResponseCacheControl: 'no-cache'
    });

    const getResponse = await s3Client.send(getCommand, { abortSignal: signal });

    if (signal?.aborted) {
      throw new Error('Request was aborted');
    }

    const body = await getResponse.Body?.transformToString('utf-8');

    if (body) {
      const data = JSON.parse(body) as JSON;
      const duration = getTimeDifference(startTime);
      logger.info(`S3 file fetched: ${s3Key}`, {
        logType: 's3-file-fetch',
        s3Bucket: bucket,
        s3Key: s3Key,
        s3Region: ENVIRONMENT_VARIABLES.env.Region,
        fileSizeBytes: getResponse.ContentLength || 0,
        durationMs: duration
      });
      return data;
    } else {
      throw new Error(`Failed to download content file from S3 bucket ${bucket}`);
    }
  } catch (error: any) {
    if (signal?.aborted || error.name === 'AbortError') {
      const abortError = new Error('Request was aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }

    // Handle NotFound (previously caught by HeadObject check)
    if (error.name === 'NoSuchKey' || error.$metadata?.httpStatusCode === 404) {
      throw new Error(eErrorMessages.NO_DATA_FOUND);
    }

    logger.error('Error while downloading file from S3:', error);
    throw error;
  }
};

/**
 * Uploads data to S3 as either a JSON or text file.
 * @param bucketName The name of the S3 bucket.
 * @param key The key for the stored file in the S3 bucket.
 * @param data The data to upload. Can be any type.
 * @param isJson Whether the data should be uploaded as a JSON file.
 * @param metadata Optional metadata for the S3 object.
 * @returns The response from the S3 PutObject command.
 */
export const uploadToS3 = async (
  bucketName: string,
  key: string,
  data: any,
  isJson: boolean,
  metadata?: Record<string, string>
): Promise<PutObjectCommandOutput> => {
  try {
    const s3Client = await getS3Client();
    const content = JSON.stringify(data);

    const putObjectCommand = new PutObjectCommand({
      Bucket: bucketName,
      Key: key,
      Body: content,
      ContentType: isJson ? 'application/json' : 'text/plain',
      Metadata: metadata
    });

    const s3Response: PutObjectCommandOutput = await s3Client.send(putObjectCommand);

    logger.info(`${isJson ? 'JSON' : 'Text'} file uploaded successfully to s3://${bucketName}/${key}`);
    logger.info(`Response Metadata: Request ID: ${s3Response.$metadata.requestId}, HTTP Status: ${s3Response.$metadata.httpStatusCode}`);

    return s3Response;
  } catch (error: any) {
    logger.error(`Error uploading ${isJson ? 'JSON' : 'text'} to S3`, error);
    throw error;
  }
};

/**
 * Deletes a file from S3.
 * @param bucketName The name of the S3 bucket.
 * @param key The key of the file to delete in the S3 bucket.
 * @returns A structured response indicating the result of the deletion attempt.
 * @throws Will throw an error for any S3 errors except NoSuchKey.
 */
export const deleteFromS3 = async (bucketName: string, key: string): Promise<S3DeleteResult> => {
  const s3Path = `s3://${bucketName}/${key}`;

  try {
    const s3Client = await getS3Client();
    const deleteObjectCommand = new DeleteObjectCommand({
      Bucket: bucketName,
      Key: key
    });

    const s3Response = await s3Client.send(deleteObjectCommand);

    logger.info(`File deleted successfully from ${s3Path}`);
    logger.info('Response Metadata:', {
      requestId: s3Response.$metadata.requestId,
      httpStatusCode: s3Response.$metadata.httpStatusCode
    });

    return {
      success: true,
      fileExisted: true,
      message: `File successfully deleted from ${s3Path}`,
      metadata: {
        requestId: s3Response.$metadata.requestId,
        httpStatusCode: s3Response.$metadata.httpStatusCode
      }
    };
  } catch (error) {
    if (error instanceof S3ServiceException) {
      switch (error.name) {
        case 'NoSuchKey':
          logger.warn(`File not found in S3: ${s3Path}`);
          return {
            success: true,
            fileExisted: false,
            message: `File did not exist: ${s3Path}`
          };

        case 'NoSuchBucket':
          throw new Error(`Bucket does not exist: ${bucketName}`);

        case 'AccessDenied':
          throw new Error(`Access denied to ${s3Path}`);

        default:
          logger.error(`S3 service error while deleting ${s3Path}:`, {
            errorName: error.name,
            errorMessage: error.message
          });
          throw error;
      }
    }

    logger.error(`Unexpected error while deleting ${s3Path}:`, { error: error });
    throw error;
  }
};
